//-- Copyright (c) 2021 TMT Insights Group, LLC
import { DataTableConfig, Titles } from '@/__configs/config_types';
import { getEndOfWeek, getStartOfWeek } from '@/_utils/date';
import defaultFilters from '@/components/_common/Filters/FilterDictionary';
import { DeepPartial } from '@/redux/slices/slice_types';

const _userConfig = {};

export enum ParamsOptions {
  VFS_TASK_TYPES = 'vfsTaskTypes'
}

export const setUserFilters = (config: any) => {
  Object.assign(_userConfig, config.filters);
};

const titlesDataConfig: DeepPartial<Titles> = {
  baseUrl: '/inventory/titles/',
  label: 'titles',
  title: 'Titles',
  filters: [
    // are you here looking for ui_entity_types_filter?
    // the reason it existed was so that we could have an "All" state on the type filter
    // since seasons should not appear in an "All" view. ui_entity_types_filter was added
    // to control the actual filter chip. see tanya for more info if needed
    defaultFilters.entityType, // to use ui_entity_types_filter, spread a new id over this object
    defaultFilters.search
  ],
  preset: {
    sorting: {
      id: 'name',
      order: 'asc'
    },
    filters: {
      entity_types_filter: ['series', 'feature']
    }
  },
  params: {
    entity_types_filter: ['feature', 'series', 'episode'],
    thumbnails: true
  },
  selected: []
};

// baseUrl = the base url for the table that `/search` is appended to
// apiBaseUrl = the base url for the API endpoint if different from main baseUrl (/api/v1 in most cases)
// title = the title of the table that appears beneath the header/navbar
// label = the label used when referring to items in the table
// filters = filters that appear above the table
//           default options: status, managedBy, createdAt, filterId, search, jobType, stage,
//                            dueDate, completedAt, linearTxDate, qcByDate, qcOnPrem, customer,
//                            profile, assignedUsers, showResolved, showNotReady, filterIdNoJobAsset,
//                            fileType, entityType, showNotReady, showResolved
// preset = the default params to apply when the table is loaded that can be replaced by filters, such as sorting/ordering
// params = params to apply to the table when it is loaded that are applied to every request, cannot be replaced by filters
// disableExport = disable the export button when filters are applied
// parseDataFn = function to parse the data from the response before it is displayed in the table,
//               and should return the array of items to display in the table, eg. (data: any) => data?.response?.items

const dataConfig: DeepPartial<DataTableConfig> = {
  orders: {
    baseUrl: '/core/orders/',
    title: 'Orders',
    label: 'orders',
    filters: [
      defaultFilters.status,
      defaultFilters.customer,
      defaultFilters.managedBy,
      defaultFilters.createdAt,
      defaultFilters.filterId,
      defaultFilters.search
    ],
    preset: {
      sorting: {
        id: 'created_at',
        order: 'desc'
      }
    },
    selected: []
  },
  jobs: {
    baseUrl: '/core/jobs/',
    title: 'Jobs',
    label: 'jobs',
    filters: [
      defaultFilters.status,
      defaultFilters.stage,
      defaultFilters.customer,
      defaultFilters.jobType,
      defaultFilters.managedBy,
      defaultFilters.filterId,
      defaultFilters.search
    ],
    preset: {
      sorting: {
        id: 'created_at',
        order: 'desc'
      }
    },
    selected: []
  },
  tasks: {
    baseUrl: '/core/tasks/',
    title: 'Tasks',
    label: 'tasks',
    filters: [
      defaultFilters.taskType,
      defaultFilters.status,
      defaultFilters.assignedUsers,
      defaultFilters.dueDate,
      defaultFilters.createdAt,
      defaultFilters.showNotReady,
      defaultFilters.filterIdNoJobAsset,
      defaultFilters.search
    ],
    params: {
      name_filter: ['in_review', 'qc', 'in_approval', 'edit']
    },
    preset: {
      sorting: {
        id: 'priority',
        order: 'asc'
      }
    },
    selected: []
  },
  pendingAssets: {
    baseUrl: '/core/jobs/assets/',
    title: 'Pending Assets',
    label: 'pending assets',
    filters: [defaultFilters.status, defaultFilters.assignedUsers, defaultFilters.dueDate, defaultFilters.filterId, defaultFilters.search],
    preset: {
      sorting: {
        id: 'id',
        order: 'desc'
      }
    },
    selected: []
  },
  processingTasks: {
    baseUrl: '/core/tasks/',
    title: 'Processing Exceptions',
    label: 'processing exceptions',
    filters: [defaultFilters.showResolved, defaultFilters.dueDate, defaultFilters.createdAt, defaultFilters.filterId, defaultFilters.search],
    params: {
      status_filter: ['in_error', 'failed']
    },
    preset: {
      sorting: {
        id: 'created_at',
        order: 'desc'
      }
    },
    selected: []
  },
  titles: titlesDataConfig,
  titlesV2: {
    ...titlesDataConfig,
    title: 'Titles'
  },
  fileIngest: {
    title: 'Ingest Manager',
    baseUrl: '/inventory/file_ingests/',
    label: 'files',
    filters: [defaultFilters.fileType, defaultFilters.stage, defaultFilters.status, defaultFilters.createdAt, defaultFilters.search],
    preset: {
      sorting: {
        id: 'created_at',
        order: 'desc'
      }
    },
    selected: []
  },
  transferQueue: {
    title: 'Transfers',
    baseUrl: '/grunt/tasks/',
    label: 'transfers',
    filters: [defaultFilters.status, defaultFilters.transferQueueAction, defaultFilters.search],
    preset: {
      sorting: {
        id: 'created',
        order: 'desc'
      }
    },
    params: {
      task_type_filter: ParamsOptions.VFS_TASK_TYPES
    },
    parseDataFn: (data: any) => data?.response?.items,
    selected: []
  },
  profiles: {
    title: 'Profiles',
    baseUrl: '/core/profile_templates/',
    label: 'profiles',
    filters: [defaultFilters.customerWithProfiles, defaultFilters.search],
    disableExport: false,
    selected: []
  },
  assemblyTemplates: {
    title: 'Assembly Templates',
    baseUrl: '/assembly_templates/',
    label: 'assembly templates',
    filters: [defaultFilters.search],
    selected: []
  },
  jobDetailTasks: {
    params: {
      name_filter: ['in_review', 'qc', 'in_approval', 'edit']
    },
    disableGetData: true,
    selected: []
  },
  jobDetailBundleJobs: {
    baseUrl: '/core/jobs/',
    title: 'Bundle Jobs',
    label: 'bundle jobs',
    selected: []
  },
  titleDetailJobs: {
    title: 'Title Detail Jobs',
    baseUrl: '/core/jobs/',
    filters: [defaultFilters.status, defaultFilters.search, defaultFilters.titleHidFilter, defaultFilters.versionHidFilter],
    selected: []
  },
  titleDetailAvails: {
    title: 'Title Detail Avails',
    baseUrl: '/inventory/titles/windows/',
    filters: [defaultFilters.status, defaultFilters.versionHidFilter],
    selected: []
  },
  exceptions: {
    baseUrl: '/monitoring/events/',
    title: 'Exceptions',
    label: 'exceptions',
    filters: [
      defaultFilters.exceptionsType,
      defaultFilters.exceptionsSubtype,
      defaultFilters.exceptionsReferenceType,
      defaultFilters.exceptionsSeverity,
      defaultFilters.customer,
      defaultFilters.status,
      defaultFilters.createdAt,
      defaultFilters.resolvedAt,
      defaultFilters.filterIdException,
      defaultFilters.search
    ],
    preset: {
      sorting: {
        id: 'created_at',
        order: 'desc'
      }
    }
  },
  focusAvails: {
    baseUrl: '/focus/avails/',
    title: 'Avails',
    label: 'avails',
    filters: [
      defaultFilters.avails_territory,
      defaultFilters.avails_timed_text,
      defaultFilters.avails_audio,
      defaultFilters.avails_license,
      defaultFilters.avails_platform,
      defaultFilters.avails_language,
      defaultFilters.term_start,
      //defaultFilters.term_end,
      defaultFilters.search
    ],
    quickFilters: [
      defaultFilters.avails_territory,
      defaultFilters.avails_timed_text,
      defaultFilters.avails_audio,
      defaultFilters.avails_platform,
      defaultFilters.term_start
    ],
    anyAllFilters: [defaultFilters.avails_timed_text, defaultFilters.avails_audio, defaultFilters.avails_territory],
    preset: {
      sorting: {
        id: 'term_start',
        order: 'desc'
      }
    }
  },
  focusSchedule: {
    baseUrl: '/focus/version-scheduling/',
    apiBaseUrl: '/api/v1.1',
    title: 'Schedule',
    label: 'schedules',
    filters: [
      defaultFilters.avails_platform,
      defaultFilters.term_start,
      defaultFilters.avails_schedule_status,
      defaultFilters.search,
      defaultFilters.title_alt_client_id_filter
    ],
    quickFilters: [defaultFilters.avails_platform, defaultFilters.term_start, defaultFilters.avails_schedule_status],
    preset: {
      sorting: {
        id: 'id',
        order: 'desc'
      }
    }
  },
  focusCalendar: {
    baseUrl: '/focus/scheduling/v2/calendar/',
    apiBaseUrl: '/api/v1.1',
    title: 'Calendar',
    label: 'calendar',
    tableType: 'calendar',
    filters: [defaultFilters.term_start, defaultFilters.avails_platform, defaultFilters.search],
    quickFilters: [defaultFilters.avails_platform],
    preset: {
      filters: {
        term_start: {
          term_start__gte: getStartOfWeek().toISOString(),
          term_start__lte: getEndOfWeek().toISOString()
        }
      }
    }
  },
  focusReports: {
    baseUrl: '/focus/reports/',
    title: 'Reports',
    label: 'reports',
    filters: [defaultFilters.search],
    // quickFilters: [defaultFilters.createdAt],
    preset: {
      sorting: {
        id: 'created_at',
        order: 'desc'
      }
    }
  }
};

export default dataConfig;
